import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react"
import { Button, Modal } from "react-bootstrap"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import ConstructionIcon from "@mui/icons-material/Construction"
import {
    CircularProgress,
    FormControlLabel,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Switch,
    useMediaQuery,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import CloseIcon from "@mui/icons-material/Close"
import { useDispatch, useSelector } from "react-redux"
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from "react-table"
import { useRowSelect } from "react-table/dist/react-table.development"
import { useDatatableCtx } from "../../components/Datatable/DatatableContext"
import { renderStyleSheet } from "../../css/styles"
import { selectWebsiteData } from "../../redux/general/selectors"
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import UnfoldMoreIcon from "@mui/icons-material/SwapVert"
import _ from "lodash"
import { setFilteredData as setFilteredDataTemplate } from "../../redux/templateData/actions"
import { Box, IconButton } from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import HistoryIcon from "@mui/icons-material/History"
import NearMeIcon from "@mui/icons-material/NearMe"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import moment from "moment"
import "moment-timezone"
import SendIcon from "@mui/icons-material/Send"
import SettingsIcon from "@mui/icons-material/Settings"
import RefreshIcon from "@mui/icons-material/Refresh"
import AddIcon from "@mui/icons-material/Add"
import "./datatable.css"
import Slider from "@mui/material/Slider"
import CustomModal from "../../components/Modal"
import { CSVDownload } from "react-csv"
import {
    getActionIconValue,
    hiddenColumns as metaDataHiddenColumns,
    isAssetDetailScreen,
    isPointer,
    renderFilter,
    renderProgressBar,
    showModalWithProperScrollBar,
    updateLocalStoragePageSize,
    applyFillToSvg,
    getSlugFromURL,
    renderTableCellContent,
    renderLockBtn,
    toTitleCase,
} from "../../utils"
import AppRegistrationIcon from "@mui/icons-material/AppRegistration"
import InfoIcon from "@mui/icons-material/Info"
import { TbAuth2Fa } from "react-icons/tb"
import LockPersonIcon from "@mui/icons-material/LockPerson"
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts"
import Tooltip from "@mui/material/Tooltip"
import { FaFile, FaFilterCircleXmark } from "react-icons/fa6"
import SaveIcon from "@mui/icons-material/Save"
import RouteIcon from "@mui/icons-material/Route"
import PageHeaderBar from "../../components/PageHeaderBar"
import { useViewMode } from "../../context/ViewModeContext.js"
import ColumnToggleSelect from "../../components/Datatable/ColumnToggleSelect.jsx"
import ModalComponent from "../../components/Modal"
import { formatDashboardHeaderKey } from "../WebsiteData/Statistics/interpreter.ts"
import { useLocalization } from "../../providers/LocalizationProvider.js"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import MapIcon from "@mui/icons-material/Map"
import TableViewIcon from "@mui/icons-material/TableView"
import { useRoutePermission } from "../../context/RoutePermissionContext"
import MenuBookIcon from "@mui/icons-material/MenuBook"

function fuzzyTextFilterFn(rows, id, filterValue) {
    return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] })
}

fuzzyTextFilterFn.autoRemove = (val) => !val

/**
 * Checks whether a filter value should be treated as empty.
 * @param {*} value - Candidate filter value.
 * @returns {boolean} True when the value should not be applied as a filter.
 */
function getIsEmptyFilterValue(value) {
    return (
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
    )
}

/**
 * Resolves dynamic default filter tokens into concrete filter values.
 * @param {*} value - Configured default filter value.
 * @param {object} column - React Table column instance.
 * @returns {*} Normalized filter value.
 */
function getNormalizedDefaultFilterValue(value, column) {
    if (column?.filters?.filterType === "date" && typeof value === "string") {
        const normalizedValue = value.trim().toLowerCase()

        if (["today", "current_date", "current_day", "now"].includes(normalizedValue)) {
            return moment().format("YYYY-MM-DD")
        }
    }

    return value
}

/**
 * Gets the metadata-driven default filter value for a column.
 * @param {object} column - React Table column instance.
 * @returns {*} Default filter value or undefined when not configured.
 */
function getColumnDefaultFilterValue(column) {
    const configuredDefaultFilter = column?.formatting?.default_filter
    if (getIsEmptyFilterValue(configuredDefaultFilter)) {
        return undefined
    }

    if (
        typeof configuredDefaultFilter === "object" &&
        configuredDefaultFilter !== null &&
        !Array.isArray(configuredDefaultFilter)
    ) {
        if (configuredDefaultFilter.enabled === false) {
            return undefined
        }

        return getNormalizedDefaultFilterValue(configuredDefaultFilter.value, column)
    }

    return getNormalizedDefaultFilterValue(configuredDefaultFilter, column)
}

const DataTable = forwardRef(
    (
        {
            appliedFilters,
            enableHamburger = true,
            handleModalStatus,
            datatableTitle,
            columns,
            data,
            actionProperty,
            module,
            buttons,
            id,
            modalStatus,
            refreshAction,
            reviewButton,
            reviewButtonTitle = "Complete Access Review",
            addAction,
            addActionName,
            filteredResult,
            className,
            onActionClick = () => {},
            hasFilterAffectsMap,
            extraBtnAction,
            showextraActionbtn,
            extraActionbtnName,
            sortable_key,
            page_name,
            rowSelected,
            modalSize,
            actions,
            isMultiSelect,
            onSelectedRowsChange,
            modalHeading,
            modalContent,
            rdx_key,
            dataTableHeight,
            changeFilterState,
            fetchFilterValue,
            showMapViewButton = true,
            showAppLogoinTable = true,
            loading,
            deletedRecordsToggleLoading = false,
            columnConfigKey,
            showDeletedRecordsToggle = false,
            deletedRecordsToggleChecked = false,
            deletedRecordsToggleLabel = "Deleted Records",
            onDeletedRecordsToggleChange = () => {},
            ...props
        },
        ref,
    ) => {
        const [showSlider, setShowSlider] = useState(false)
        const [viewportHeight, setViewportHeight] = useState(window.innerHeight)
        const appName = sessionStorage.getItem("app_key")
        const app = sessionStorage.getItem("app")
        const pathName = window.location.pathname.replace(/\//g, "")

        // Prevent a "flash" of previous app table rows during app switches.
        // Even if `data` hasn't updated yet, we force Loading UI until the new
        // fetch result is applied.
        const [isAppSwitching, setIsAppSwitching] = useState(false)
        const prevAppNameRef = React.useRef(appName)
        const prevDataRef = React.useRef(data)

        useEffect(() => {
            if (prevAppNameRef.current !== appName) {
                prevAppNameRef.current = appName
                prevDataRef.current = data
                setIsAppSwitching(true)
            }
        }, [appName, data])

        useEffect(() => {
            if (!isAppSwitching) return
            // Once the parent indicates loading is complete and `data` reference changed,
            // we can render the new table content.
            if (!loading && data !== prevDataRef.current) {
                setIsAppSwitching(false)
            }
        }, [isAppSwitching, loading, data])

        const effectiveLoading = loading || isAppSwitching

        const websiteData = useSelector(selectWebsiteData)
        renderStyleSheet(websiteData)
        const [rangeSliderValue, setValue] = React.useState([0, 100])
        const [rangeSliderModal, setRangeSliderModal] = useState(false)
        const [csvData, setCsvData] = useState(null)
        const [actionCount, setActionCount] = useState(0)
        const [columnConfigureModal, setColumnConfigureModal] = useState(false)
        const { voiceiiTags } = useLocalization()
        const { canManage: canManageRouteActions } = useRoutePermission()

        const handleRangeSliderChange = (event, newValue) => {
            setValue(newValue)
            setFilter("percent_full", newValue)
        }

        const maxCount = useMemo(() => {
            if (!data) return 0
            return data.reduce((max, el) => Math.max(max, el?.action_properties?.length || 0), 0)
        }, [data])

        useEffect(() => {
            setActionCount(maxCount)
        }, [maxCount])

        const { row, columnMeta, heading } = useDatatableCtx()
        const dispatch = useDispatch()

        const filterTypes = React.useMemo(
            () => ({
                fuzzyText: fuzzyTextFilterFn,
                "min-max": (rows, id, filterValue) => {
                    const filteredArray = rows.filter((row) => {
                        const rowValue = row.values[id[0]]
                        return rowValue >= filterValue[0] && rowValue <= filterValue[1]
                    })

                    return filteredArray
                },
                date: (rows, id, filterValue) => {
                    if (!filterValue) return rows

                    const selected = moment(filterValue, "YYYY-MM-DD", true)
                    if (!selected.isValid()) return rows

                    return rows.filter((row) => {
                        const cellValue = row.values[id]
                        if (!cellValue) return false

                        let cellDate = moment(cellValue)

                        if (!cellDate.isValid()) {
                            cellDate = moment(cellValue, "YYYY-MM-DD", true)
                            if (!cellDate.isValid()) return false
                        }

                        return cellDate.isSame(selected, "day")
                    })
                },
                text: (rows, id, filterValue) =>
                    rows.filter((row) => {
                        const rowValue = row.values[id]
                        return rowValue !== undefined
                            ? String(rowValue)
                                  .toLowerCase()
                                  .startsWith(String(filterValue).toLowerCase())
                            : true
                    }),
            }),
            [],
        )

        const datatableId = id ? id : "_id"
        const isModalDatatable = datatableId === "modal_datatable"
        const storageKeyPrefix = `${window.location.pathname + app + datatableId}`
        const storedFilters = isModalDatatable
            ? []
            : JSON.parse(localStorage.getItem(`${storageKeyPrefix}_filter`)) || []
        const storedSorting = isModalDatatable
            ? []
            : JSON.parse(localStorage.getItem(`${storageKeyPrefix}_sort`)) || []
        const storedPageIndex = isModalDatatable
            ? 0
            : parseInt(localStorage.getItem(`${storageKeyPrefix}_pageIndex`)) || 0

        const storedData = JSON.parse(localStorage.getItem(appName)) || undefined

        const [filters, setFilters] = useState(storedFilters)
        const [sorting, setSorting] = useState(storedSorting)
        const [pageIndex, setPageIndex] = useState(storedPageIndex)

        const renderFilterMemonized = useCallback(renderFilter, [])
        let BASE_URL = process.env.REACT_APP_API_URL

        const columnConfigStorageKey = `${appName}_${columnConfigKey}_hiddenColumns`

        // 2. User-hidden (stored in localStorage)
        const [userHiddenColumns, setUserHiddenColumns] = useState(() => {
            const stored = localStorage.getItem(columnConfigStorageKey)
            return stored ? JSON.parse(stored) : []
        })

        // 3. Compute actual hiddenColumns for react-table
        const getHiddenColumns = (userHidden) => {
            return [...new Set([...metaDataHiddenColumns, ...userHidden])]
        }

        const {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            rows,
            setFilter,
            prepareRow,
            state,
            visibleColumns,
            preGlobalFilteredRows,
            setGlobalFilter,
            canPreviousPage,
            canNextPage,
            page,
            pageOptions,
            pageCount,
            gotoPage,
            nextPage,
            previousPage,
            setAllFilters,
            toggleRowSelected,
            selectedFlatRows,
            setPageSize,
            allColumns,
            setHiddenColumns,
            state: { filters: tableFilters, sortBy: tableSorting, pageSize },
            ...useTableProps
        } = useTable(
            {
                columns,
                data,
                enableRowSelection: true,
                enableMultiRowSelection: true,
                filterTypes,
                state: {
                    pageSize: storedData && storedData[pathName] ? storedData[pathName] : 10,
                },
                initialState: {
                    pageIndex,
                    hiddenColumns: getHiddenColumns(userHiddenColumns),

                    selectedRowIds: {},
                    filters,
                    sortBy: sorting,
                    pageSize: storedData && storedData[pathName] ? storedData[pathName] : 10,
                },
            },
            useFilters,
            useGlobalFilter,
            useSortBy,
            usePagination,
            useRowSelect,
        )
        const hasAppliedDefaultColumnFilters = useRef(false)

        useEffect(() => {
            if (hasAppliedDefaultColumnFilters.current) return
            if (!allColumns.length) return

            const defaultFilters = allColumns
                .map((column) => ({
                    id: column.id,
                    value: getColumnDefaultFilterValue(column),
                }))
                .filter(({ value }) => !getIsEmptyFilterValue(value))

            if (!defaultFilters.length) return

            const missingDefaultFilters = defaultFilters.filter(({ id }) => {
                const existingFilter = tableFilters.find((filter) => filter.id === id)
                return getIsEmptyFilterValue(existingFilter?.value)
            })

            if (missingDefaultFilters.length) {
                setAllFilters([
                    ...tableFilters,
                    ...missingDefaultFilters.map(({ id, value }) => ({ id, value })),
                ])
            }

            hasAppliedDefaultColumnFilters.current = true
        }, [allColumns, setAllFilters, tableFilters])

        // Notify parent when selected rows change
        useEffect(() => {
            if (isMultiSelect && onSelectedRowsChange) {
                const selectedData = selectedFlatRows.map((row) => row.original)
                onSelectedRowsChange?.(selectedData)
            }
            if (isMultiSelect && rowSelected) {
                rowSelected(selectedFlatRows)
            }
        }, [selectedFlatRows?.length])

        // persist user config
        useEffect(() => {
            localStorage.setItem(columnConfigStorageKey, JSON.stringify(userHiddenColumns))
        }, [userHiddenColumns])

        // keep in sync
        useEffect(() => {
            setHiddenColumns(getHiddenColumns(userHiddenColumns))
        }, [userHiddenColumns, setHiddenColumns])

        useEffect(() => {
            if (dataTableHeight) {
                setViewportHeight(dataTableHeight + 225)
            } else if (id == "modal_datatable") {
                setViewportHeight(670)
            }
        }, [id, dataTableHeight])
        // Sync action rail row heights with table rows
        useEffect(() => {
            if (!!actions.length || !!actionProperty?.length) {
                const syncHeights = () => {
                    const tableRows = document.querySelectorAll(
                        `#${id ? id : "datatable-scroller"} table tbody tr`,
                    )
                    const actionRows = document.querySelectorAll(`.datatable-action-rail-row`)
                    tableRows.forEach((tableRow, index) => {
                        if (actionRows[index]) {
                            const tableRowHeight = tableRow.offsetHeight
                            actionRows[index].style.height = `${tableRowHeight}px`
                        }
                    })
                }
                syncHeights()
                // Re-sync on window resize
                window.addEventListener("resize", syncHeights)
                return () => window.removeEventListener("resize", syncHeights)
            }
        }, [page, actions.length, actionProperty?.length, id, loading])

        useImperativeHandle(ref, () => ({
            headerGroups,
            clearFilter,
            updateFilter: (filters) => {
                setFilters([...filters])
            },
        }))

        useEffect(() => {
            const handleResize = () => {
                setViewportHeight(window.innerHeight)
            }

            window.addEventListener("resize", handleResize)

            return () => {
                window.removeEventListener("resize", handleResize)
            }
        }, [])

        useEffect(() => {
            if (pageIndex > pageOptions.length - 1) {
                setPageIndex(0)
            }
        }, [pageIndex, pageOptions])

        useEffect(() => {
            setFilters([...tableFilters])
        }, [tableFilters])

        useEffect(() => {
            setSorting(tableSorting)
        }, [tableSorting])

        useEffect(() => {
            setPageIndex(state.pageIndex)
        }, [state.pageIndex])

        useEffect(() => {
            if (!isModalDatatable) return
            if (loading) return
            if (!Array.isArray(data) || data.length === 0) return

            gotoPage(0)
            setPageIndex(0)
        }, [data, gotoPage, isModalDatatable, loading])

        useEffect(() => {
            if (isModalDatatable) return

            localStorage.setItem(`${storageKeyPrefix}_filter`, JSON.stringify(filters))
            localStorage.setItem(`${storageKeyPrefix}_sort`, JSON.stringify(sorting))
            localStorage.setItem(`${storageKeyPrefix}_pageIndex`, pageIndex)
        }, [filters, isModalDatatable, pageIndex, sorting, storageKeyPrefix])

        useEffect(() => {
            if (data.length > 0 && hasFilterAffectsMap == true) {
                const debounceEffect = setTimeout(() => {
                    if (state.filters.length > 0) {
                        dispatch(setFilteredDataTemplate(rows.map((data) => data.original)))
                    } else dispatch(setFilteredDataTemplate(data))
                }, 1000)

                return () => {
                    clearTimeout(debounceEffect)
                }
            }
        }, [state.filters, data, hasFilterAffectsMap, rows])

        const clearFilter = () => {
            setFilters([])
            setSorting([])
            setPageIndex(0)
            if (!isModalDatatable) {
                localStorage.setItem(`${storageKeyPrefix}_filter`, JSON.stringify([]))
                localStorage.setItem(`${storageKeyPrefix}_sort`, JSON.stringify([]))
                localStorage.setItem(`${storageKeyPrefix}_pageIndex`, 0)
            }
            setAllFilters([])
            setPageIndex(0)
            setPageSize(10)
            setValue([0, 100])
        }

        const renderedHeaders = []

        const convertToCSV = () => {
            headerGroups.map((headerGroup, index) => {
                headerGroup.headers.map((column, index2) => {
                    renderedHeaders.push(column.id)
                })
            })

            const csvRows = rows.map((row) => {
                const tempObject = {}
                renderedHeaders.forEach((key) => {
                    if (row.values.hasOwnProperty(key)) {
                        if (typeof row.values[key] === "string" && row.values[key].includes(",")) {
                            tempObject[key] = row.values[key].replace(/, /g, " ")
                        } else {
                            tempObject[key] = row.values[key]
                        }
                    }
                })

                return renderedHeaders.map((key) => tempObject[key] || "N/A").join(",")
            })

            const csvString = [...[renderedHeaders.join(",")], ...csvRows].join("\n")
            setCsvData(csvString)
        }

        useEffect(() => {
            if (id) {
                document.getElementById(id).scrollLeft = 0
            } else document.getElementById("datatable-scroller").scrollLeft = 0
        }, [data.length, id])

        const isWavsmart = BASE_URL === "https://wavsmart.efleety.com/"
        const defaultPrimary = isWavsmart ? "#00bc7f" : "#000000"
        const primaryColor = websiteData.primary_colour || defaultPrimary
        const iconsColor = websiteData.primary_colour || defaultPrimary

        const { viewMode, toggleViewMode, canToggle } = useViewMode()
        const [anchorEl, setAnchorEl] = useState(null)
        const [activeRow, setActiveRow] = useState(null)
        const theme = useTheme()
        const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

        const handleMenuOpen = (event, row) => {
            setAnchorEl(event.currentTarget)
            setActiveRow(row)
        }

        const handleMenuClose = () => {
            setAnchorEl(null)
            setActiveRow(null)
        }

        return (
            <>
                <style>
                    {`
                :root {
                    --dt-primary: ${primaryColor};
                    --dt-primary-transparent: ${primaryColor}15;
                    --dt-border-subtle: rgba(0, 0, 0, 0.05);
                    --dt-text-header: ${primaryColor};
                    --dt-text-body: #333;
                    --dt-bg-header: ${primaryColor}08;
                }

                .main-datatable-wrapper {
                    background: #fff;
                    border-radius: 8px;
                }

                .table.dataTable thead th {
                    color: var(--dt-text-header) !important;
                    font-weight: 600 !important;
                    text-transform: capitalize;
                    border-bottom: 1px solid var(--dt-border-subtle) !important;
                    background-color: var(--dt-bg-header) !important;
                    padding: 12px 16px !important;
                }

                .table.dataTable tbody td {
                    color: var(--dt-text-body);
                    border-bottom: 1px solid var(--dt-border-subtle) !important;
                    padding: 12px 16px !important;
                    vertical-align: middle !important;
                }

                .table.dataTable tbody tr:hover {
                    background-color: var(--dt-primary-transparent) !important;
                }

                .table-filter-input, .table-filter-select {
                    border: 1px solid var(--dt-border-subtle) !important;
                    border-radius: 6px !important;
                    height: 35px !important;
                    font-size: 14px !important;
                    padding: 8px 12px !important;
                    width: fit-content !important;
                    background-color: #fff !important;
                }

                .actionsPanel {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 5px;
                    padding: 8px;
                }

                .dynamic-icon-btn {
                    color: var(--dt-primary) !important;
                    padding: 8px !important;
                    transition: all 0.2s ease;
                    margin-right: 0px;
                }

                .dynamic-icon-btn:hover {
                    background-color: var(--dt-primary-transparent) !important;
                    transform: scale(1.1);
                }

                .datatable-header-toggle {
                    margin: 0 4px 0 2px;
                    color: var(--dt-primary);
                    white-space: nowrap;
                }

                .datatable-toggle-btn-wrapper {
                    width: 40px;
                    height: 40px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0;
                    border-radius: 6px;
                    margin-right: 10px;
                    border: 1px solid var(--dt-primary);
                    background: var(--dt-primary);
                    transition: all 0.2s ease;
                }

                .datatable-toggle-btn-wrapper:hover {
                    filter: brightness(0.95);
                }

                .datatable-toggle-btn-wrapper .datatable-header-toggle {
                    margin: 0;
                }

                .datatable-toggle-btn-wrapper .MuiFormControlLabel-root {
                    margin: 0;
                }

                .datatable-toggle-btn-wrapper .MuiSwitch-root {
                    margin: 0;
                    padding: 0;
                    transform: scale(0.72);
                    transform-origin: center;
                }

                @media (max-width: 1000px) {
                        .datatable-toggle-btn-wrapper {
                            width: 32px;
                            height: 26px;
                            margin-right: 8px;
                    }

                    .datatable-toggle-btn-wrapper .MuiSwitch-root {
                        transform: scale(0.64);
                    }
                }

                .datatable-toggle-btn-wrapper .MuiSwitch-switchBase {
                    color: #fff;
                }

                .datatable-toggle-btn-wrapper .MuiSwitch-switchBase.Mui-checked {
                    color: #fff;
                }

                .datatable-toggle-btn-wrapper .MuiSwitch-track {
                    background-color: rgba(255, 255, 255, 0.5) !important;
                    opacity: 1 !important;
                    border-radius: 999px;
                }

                .datatable-toggle-btn-wrapper
                    .MuiSwitch-switchBase.Mui-checked
                    + .MuiSwitch-track {
                    background-color: rgba(255, 255, 255, 0.75) !important;
                    opacity: 1 !important;
                }

                .datatable-header-toggle .MuiFormControlLabel-label {
                    font-size: 12px;
                    font-weight: 600;
                    color: var(--dt-primary);
                }

                .pagination-wrapper {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 24px;
                    border-top: 1px solid var(--dt-border-subtle);
                    font-size: 14px;
                    color: #666;
                }
                    @media (max-width: 767px) {
                        .pagination-wrapper {
                            flex-direction: column;
                            gap: 12px;
                        }
                    }

                .pagination-info {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                @media (max-width: 767px) {
                    .pagination-info {
                        width: 100%;
                        justify-content: space-between;
                    }
                }

                .pagination-controls {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
                 @media (max-width: 767px) {
                    .pagination-controls {
                        width: 100%;
                        justify-content: space-between;
                    }
                }

                .page-link-custom {
                    min-width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    border: 1px solid transparent;
                }

                .page-link-custom.active {
                    background-color: var(--dt-primary);
                    color: #fff;
                }

                .page-link-custom:hover:not(.active) {
                    background-color: var(--dt-primary-transparent);
                }
                `}
                </style>
                <Box
                    className="main-datatable-wrapper"
                    style={{
                        padding: "0",
                        // height: showModalWithProperScrollBar(page_name) ? "590px" : "370px",
                        overflowY: page_name && page_name === "iot_history" ? "hidden" : "none",
                    }}
                >
                    {datatableTitle && (
                        <PageHeaderBar
                            title={datatableTitle}
                            translateTitle={false}
                            showHamburger={enableHamburger}
                            showLogo={showAppLogoinTable}
                            actions={
                                <Box className="d-flex align-items-center">
                                    {!!reviewButton && canManageRouteActions && (
                                        <>
                                            <button
                                                onClick={reviewButton}
                                                className="btn text-capitalize btn-secondary e-table-review-button"
                                            >
                                                {reviewButtonTitle?.split("_").join(" ")}
                                            </button>
                                        </>
                                    )}
                                    {showDeletedRecordsToggle && (
                                        <Tooltip
                                            title={
                                                deletedRecordsToggleChecked
                                                    ? voiceiiTags(
                                                          `Hide ${deletedRecordsToggleLabel}`,
                                                      )
                                                    : voiceiiTags(
                                                          `Show ${deletedRecordsToggleLabel}`,
                                                      )
                                            }
                                            placement="bottom"
                                        >
                                            <div className="datatable-toggle-btn-wrapper">
                                                {deletedRecordsToggleLoading ? (
                                                    <CircularProgress
                                                        size={16}
                                                        thickness={5}
                                                        style={{ color: "#fff" }}
                                                    />
                                                ) : (
                                                    <FormControlLabel
                                                        className="datatable-header-toggle m-0"
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    deletedRecordsToggleChecked
                                                                }
                                                                onChange={(event) =>
                                                                    onDeletedRecordsToggleChange(
                                                                        event.target.checked,
                                                                        event,
                                                                    )
                                                                }
                                                                size="small"
                                                                inputProps={{
                                                                    "aria-label":
                                                                        voiceiiTags(
                                                                            deletedRecordsToggleLabel,
                                                                        ),
                                                                }}
                                                                color="primary"
                                                            />
                                                        }
                                                        // label={voiceiiTags(deletedRecordsToggleLabel)}
                                                    />
                                                )}
                                            </div>
                                        </Tooltip>
                                    )}
                                    {data.length > 0 && (
                                        <Tooltip
                                            title={voiceiiTags("Download CSV")}
                                            placement="bottom"
                                        >
                                            <button
                                                onClick={convertToCSV}
                                                className="btn btn-secondary e-table-top-right-button"
                                            >
                                                <FileDownloadIcon style={{ color: "#fff" }} />
                                            </button>
                                        </Tooltip>
                                    )}

                                    {csvData && data.length > 0 && (
                                        <CSVDownload
                                            data={csvData}
                                            filename={"table_data.csv"}
                                            className="btn btn-primary"
                                            target="_blank"
                                        >
                                            Download CSV
                                        </CSVDownload>
                                    )}
                                    {!!state.filters.length && (
                                        <Tooltip
                                            title={voiceiiTags("Clear Filter")}
                                            placement="bottom"
                                        >
                                            <button
                                                onClick={() => clearFilter()}
                                                className="btn btn-secondary e-table-top-right-button"
                                            >
                                                <FaFilterCircleXmark
                                                    style={{ color: "#fff", fontSize: 22 }}
                                                />
                                            </button>
                                        </Tooltip>
                                    )}
                                    {!!refreshAction &&
                                        datatableTitle !== "Device History" &&
                                        data.length >= 0 && (
                                            <>
                                                <Tooltip
                                                    title={voiceiiTags("Refresh Table")}
                                                    placement="bottom"
                                                >
                                                    <button
                                                        onClick={refreshAction}
                                                        style={{
                                                            height: "40px",
                                                            padding: "0.5rem",
                                                        }}
                                                        className="btn btn-secondary e-table-top-right-button"
                                                    >
                                                        <RefreshIcon className="e-table-view-mode-button-icon" />
                                                    </button>
                                                </Tooltip>
                                            </>
                                        )}
                                    {!!addAction && canManageRouteActions && (
                                        <>
                                            {addActionName !== "Routing" ? (
                                                <Tooltip
                                                    title={`${addActionName}`}
                                                    placement="bottom"
                                                >
                                                    <button
                                                        onClick={addAction}
                                                        className="btn btn-secondary e-table-top-right-button"
                                                    >
                                                        <AddIcon className="e-table-view-mode-button-icon" />
                                                    </button>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip
                                                    title={`${addActionName}`}
                                                    placement="bottom"
                                                >
                                                    <button
                                                        onClick={addAction}
                                                        className="btn btn-secondary e-table-top-right-button"
                                                    >
                                                        {addActionName === "Routing" ? (
                                                            <RouteIcon size={18} />
                                                        ) : (
                                                            addActionName
                                                        )}
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                    {!!extraBtnAction &&
                                        showextraActionbtn &&
                                        canManageRouteActions && (
                                            <>
                                                <Tooltip
                                                    title={`${extraActionbtnName}`}
                                                    placement="bottom"
                                                >
                                                    <button
                                                        onClick={extraBtnAction}
                                                        className="btn btn-secondary responsive-btn e-table-save-button"
                                                    >
                                                        {extraActionbtnName === "Save" ? (
                                                            <SaveIcon size={20} />
                                                        ) : (
                                                            extraActionbtnName
                                                        )}
                                                    </button>
                                                </Tooltip>
                                                <button
                                                    onClick={extraBtnAction}
                                                    className="btn btn-secondary responsive-btn-icon e-table-save-button-icon"
                                                >
                                                    <AddIcon />
                                                </button>
                                            </>
                                        )}

                                    {canManageRouteActions &&
                                        buttons.map((button) => {
                                            return (
                                                <>
                                                    <button
                                                        onClick={button.action}
                                                        className="btn btn-secondary responsive-btn e-table-top-right-button"
                                                        disabled={button.disabled}
                                                    >
                                                        {button.text}
                                                    </button>
                                                    <button
                                                        onClick={button.action}
                                                        className="btn btn-secondary responsive-btn-icon e-table-save-button-icon"
                                                        disabled={button.disabled}
                                                    >
                                                        {button.icon}
                                                    </button>
                                                </>
                                            )
                                        })}

                                    {canToggle && showMapViewButton && (
                                        <div className="map-toggle-btn d-flex">
                                            <Tooltip
                                                title={
                                                    viewMode === "map_view"
                                                        ? voiceiiTags("Table View")
                                                        : voiceiiTags("Map View")
                                                }
                                                placement="bottom"
                                            >
                                                <button
                                                    className="view_mode btn btn-secondary e-table-top-right-button"
                                                    onClick={toggleViewMode}
                                                >
                                                    {viewMode === "map_view" ? (
                                                        <TableViewIcon className="e-table-view-mode-button-icon" />
                                                    ) : (
                                                        <MapIcon className="e-table-view-mode-button-icon" />
                                                    )}
                                                </button>
                                            </Tooltip>
                                        </div>
                                    )}
                                </Box>
                            }
                        />
                    )}
                    <div style={{ padding: id == "modal_datatable" ? "5px" : "10px" }}>
                        <div
                            id={id ? id : "datatable-scroller"}
                            style={{
                                // marginTop: "20px",// old
                                marginTop: "10px",
                                height: showModalWithProperScrollBar(page_name) // page_name === "iot_history"
                                    ? "400px"
                                    : `${viewportHeight - 225}px`,
                                overflowY: "auto",
                                overflowX: "hidden",
                                maxHeight: dataTableHeight
                                    ? `${dataTableHeight}px`
                                    : id == "modal_datatable"
                                      ? "19rem"
                                      : "100%",
                            }}
                            className="e-table px-md-5 pb-5"
                        >
                            <div className={`${className}`}>
                                {effectiveLoading ? (
                                    <p className="data-not-found">{voiceiiTags("Loading...")}</p>
                                ) : data?.length === 0 &&
                                  page?.length === 0 &&
                                  !effectiveLoading ? (
                                    <p className="data-not-found">
                                        {voiceiiTags("No records found")}
                                    </p>
                                ) : (
                                    <div className="datatable-root">
                                        <div className="datatable-scroll">
                                            <table
                                                {...getTableProps()}
                                                className="table stripe text-nowrap border-bottom"
                                            >
                                                <thead
                                                    style={{
                                                        backgroundColor: "#F0F5F9",
                                                        color: iconsColor,
                                                    }}
                                                >
                                                    {headerGroups.map((headerGroup, index) => (
                                                        <tr
                                                            {...headerGroup.getHeaderGroupProps()}
                                                            key={index}
                                                        >
                                                            {isMultiSelect && (
                                                                <th
                                                                    style={{
                                                                        width: "40px",
                                                                        textAlign: "center",
                                                                        verticalAlign: "middle",
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={
                                                                            page.length > 0 &&
                                                                            page.every(
                                                                                (row) =>
                                                                                    row.isSelected,
                                                                            )
                                                                        }
                                                                        onChange={(e) => {
                                                                            page.forEach((row) => {
                                                                                toggleRowSelected(
                                                                                    row.id,
                                                                                    e.target
                                                                                        .checked,
                                                                                )
                                                                            })
                                                                        }}
                                                                        style={{
                                                                            cursor: "pointer",
                                                                            width: "16px",
                                                                            height: "16px",
                                                                        }}
                                                                    />
                                                                </th>
                                                            )}
                                                            {headerGroup.headers.map(
                                                                (column, index2) => {
                                                                    // Check if column has an active filter
                                                                    const filterValue =
                                                                        filters.find(
                                                                            (f) =>
                                                                                f.id === column.id,
                                                                        )?.value
                                                                    const isColumnFiltered =
                                                                        filterValue !== undefined &&
                                                                        filterValue !== "" &&
                                                                        filterValue !== null

                                                                    return (
                                                                        <th
                                                                            key={index2}
                                                                            className={
                                                                                isColumnFiltered
                                                                                    ? "column-filter-active"
                                                                                    : ""
                                                                            }
                                                                            {...(column?.filters
                                                                                ?.filterType ===
                                                                                "range-slider" && {
                                                                                onMouseEnter: () =>
                                                                                    setShowSlider(
                                                                                        true,
                                                                                    ),
                                                                                onMouseLeave: () =>
                                                                                    setShowSlider(
                                                                                        false,
                                                                                    ),
                                                                            })}
                                                                        >
                                                                            <div
                                                                                {...column.getHeaderProps(
                                                                                    column.getSortByToggleProps(),
                                                                                )}
                                                                                className="d-flex align-items-center justify-content-start gap-2 fw-bold"
                                                                                style={{
                                                                                    color: iconsColor,
                                                                                    fontSize:
                                                                                        "14px",
                                                                                }}
                                                                            >
                                                                                <span>
                                                                                    {voiceiiTags(
                                                                                        column.render(
                                                                                            "Header",
                                                                                        ),
                                                                                    )}
                                                                                </span>
                                                                                <span
                                                                                    className="d-flex align-items-center"
                                                                                    style={{
                                                                                        color: "var(--dt-primary)",
                                                                                    }}
                                                                                >
                                                                                    {column.isSorted ? (
                                                                                        column.isSortedDesc ? (
                                                                                            <ArrowDropDownIcon fontSize="small" />
                                                                                        ) : (
                                                                                            <ArrowDropUpIcon fontSize="small" />
                                                                                        )
                                                                                    ) : (
                                                                                        <UnfoldMoreIcon
                                                                                            fontSize="small"
                                                                                            style={{
                                                                                                opacity: 0.4,
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                            {column.canFilter ? (
                                                                                <div className="e-margin-top-10px">
                                                                                    {renderFilterMemonized(
                                                                                        {
                                                                                            filterType:
                                                                                                column
                                                                                                    ?.filters
                                                                                                    ?.filterType,
                                                                                            data: column,
                                                                                            onChange:
                                                                                                (
                                                                                                    e,
                                                                                                ) => {
                                                                                                    column.setFilter(
                                                                                                        e
                                                                                                            .target
                                                                                                            .value,
                                                                                                    )
                                                                                                },
                                                                                            setShowSlider,
                                                                                            showSlider,
                                                                                            value:
                                                                                                filters.find(
                                                                                                    (
                                                                                                        f,
                                                                                                    ) =>
                                                                                                        f.id ===
                                                                                                        column.id,
                                                                                                )
                                                                                                    ?.value ||
                                                                                                "",
                                                                                            rangefilterValue:
                                                                                                filters.find(
                                                                                                    (
                                                                                                        f,
                                                                                                    ) =>
                                                                                                        f.id ===
                                                                                                        column.id,
                                                                                                )
                                                                                                    ?.value ||
                                                                                                rangeSliderValue,
                                                                                            handleRangeChange:
                                                                                                (
                                                                                                    e,
                                                                                                    newValue,
                                                                                                ) => {
                                                                                                    setValue(
                                                                                                        newValue,
                                                                                                    )
                                                                                                    setFilter(
                                                                                                        column.id,
                                                                                                        newValue,
                                                                                                    )
                                                                                                },
                                                                                            handleRangeModal:
                                                                                                () => {
                                                                                                    setRangeSliderModal(
                                                                                                        true,
                                                                                                    )
                                                                                                },
                                                                                            voiceiiTags,
                                                                                        },
                                                                                    )}
                                                                                </div>
                                                                            ) : null}
                                                                        </th>
                                                                    )
                                                                },
                                                            )}
                                                        </tr>
                                                    ))}
                                                </thead>
                                                <tbody {...getTableBodyProps()}>
                                                    {page.map((row, i) => {
                                                        prepareRow(row)

                                                        return (
                                                            <tr
                                                                key={i}
                                                                {...row.getRowProps()}
                                                                style={{
                                                                    backgroundColor:
                                                                        i % 2 === 0
                                                                            ? "white"
                                                                            : "#f0f5f9",
                                                                    ...row.getRowProps().style,
                                                                }}
                                                            >
                                                                {isMultiSelect && (
                                                                    <td
                                                                        style={{
                                                                            width: "40px",
                                                                            textAlign: "center",
                                                                            verticalAlign: "middle",
                                                                        }}
                                                                    >
                                                                        <input
                                                                            checked={row.isSelected}
                                                                            type="checkbox"
                                                                            onChange={(e) => {
                                                                                toggleRowSelected(
                                                                                    row.id,
                                                                                    e.target
                                                                                        .checked,
                                                                                )
                                                                            }}
                                                                            style={{
                                                                                cursor: "pointer",
                                                                                width: "16px",
                                                                                height: "16px",
                                                                            }}
                                                                        />
                                                                    </td>
                                                                )}
                                                                {row.cells.map((cell, index) => {
                                                                    const columnId =
                                                                        cell.column.id.toLowerCase()
                                                                    const isPercentage =
                                                                        columnId.includes(
                                                                            "percent",
                                                                        ) ||
                                                                        columnId.includes(
                                                                            "level",
                                                                        ) ||
                                                                        columnId.includes(
                                                                            "battery",
                                                                        ) ||
                                                                        columnId.includes(
                                                                            "signal_strength",
                                                                        ) ||
                                                                        columnId.includes(
                                                                            "completion",
                                                                        )

                                                                    return (
                                                                        <td
                                                                            key={index}
                                                                            {...cell.getCellProps()}
                                                                        >
                                                                            {renderTableCellContent(
                                                                                {
                                                                                    cell,
                                                                                    isPercentage,
                                                                                    renderProgressBar,
                                                                                    getSlugFromURL,
                                                                                },
                                                                            )}
                                                                        </td>
                                                                    )
                                                                })}
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* ACTION Rail - Fixed control rail */}
                                        {canManageRouteActions &&
                                            (!!actions.length || !!actionProperty?.length) && (
                                                <div className="datatable-action-rail">
                                                    {/* ACTION Header */}
                                                    <div
                                                        className="datatable-action-rail-header"
                                                        style={{ color: iconsColor }}
                                                    >
                                                        {isMobile ? (
                                                            <ConstructionIcon className="action-header-icon" />
                                                        ) : (
                                                            voiceiiTags("ACTION")
                                                        )}
                                                    </div>

                                                    {/* ACTION Rows - One per table row */}
                                                    {page.map((row, i) => {
                                                        prepareRow(row)
                                                        return (
                                                            <div
                                                                key={i}
                                                                className="datatable-action-rail-row"
                                                                data-selected={row.isSelected}
                                                            >
                                                                <div className="actionsPanel">
                                                                    {isMobile ? (
                                                                        <>
                                                                            {/* 3 DOT BUTTON */}
                                                                            <IconButton
                                                                                onClick={(e) =>
                                                                                    handleMenuOpen(
                                                                                        e,
                                                                                        row,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <MoreVertIcon />
                                                                            </IconButton>

                                                                            {/* MENU */}
                                                                            <Menu
                                                                                anchorEl={anchorEl}
                                                                                open={
                                                                                    Boolean(
                                                                                        anchorEl,
                                                                                    ) &&
                                                                                    activeRow ===
                                                                                        row
                                                                                }
                                                                                onClose={
                                                                                    handleMenuClose
                                                                                }
                                                                                anchorOrigin={{
                                                                                    vertical:
                                                                                        "bottom",
                                                                                    horizontal:
                                                                                        "right",
                                                                                }}
                                                                                transformOrigin={{
                                                                                    vertical: "top",
                                                                                    horizontal:
                                                                                        "right",
                                                                                }}
                                                                                MenuListProps={{
                                                                                    className:
                                                                                        "mobile-action-menu-list",
                                                                                }}
                                                                            >
                                                                                {/* Dynamic action_properties */}
                                                                                {row?.values?.action_properties?.map(
                                                                                    (
                                                                                        action,
                                                                                        index,
                                                                                    ) => {
                                                                                        const isHidden =
                                                                                            action?.show !==
                                                                                            true
                                                                                        if (
                                                                                            isHidden
                                                                                        ) {
                                                                                            return null
                                                                                        }

                                                                                        const iconValue =
                                                                                            getActionIconValue(
                                                                                                row.values,
                                                                                                action,
                                                                                            )
                                                                                        const isDisabled =
                                                                                            Array.isArray(
                                                                                                row
                                                                                                    ?.values
                                                                                                    ?.action_buttons_visibility,
                                                                                            ) &&
                                                                                            !row.values.action_buttons_visibility.includes(
                                                                                                action.slug ||
                                                                                                    action.value,
                                                                                            )

                                                                                        return (
                                                                                            <MenuItem
                                                                                                className="mobile-action-item"
                                                                                                disabled={
                                                                                                    isDisabled
                                                                                                }
                                                                                                key={
                                                                                                    index
                                                                                                }
                                                                                                onClick={(
                                                                                                    event,
                                                                                                ) => {
                                                                                                    handleMenuClose()
                                                                                                    onActionClick(
                                                                                                        event,
                                                                                                        {
                                                                                                            type: action.type,
                                                                                                            data: row,
                                                                                                            action_property:
                                                                                                                action,
                                                                                                        },
                                                                                                    )
                                                                                                }}
                                                                                            >
                                                                                                <ListItemIcon>
                                                                                                    {(() => {
                                                                                                        const iconValue =
                                                                                                            getActionIconValue(
                                                                                                                row.values,
                                                                                                                action,
                                                                                                            )

                                                                                                        const isSvg =
                                                                                                            typeof iconValue ===
                                                                                                                "string" &&
                                                                                                            iconValue
                                                                                                                .trim()
                                                                                                                .startsWith(
                                                                                                                    "<svg",
                                                                                                                )

                                                                                                        return isSvg ? (
                                                                                                            <span
                                                                                                                className="e-dynamic-action-icon"
                                                                                                                dangerouslySetInnerHTML={{
                                                                                                                    __html: applyFillToSvg(
                                                                                                                        iconValue,
                                                                                                                        iconsColor,
                                                                                                                    ),
                                                                                                                }}
                                                                                                            />
                                                                                                        ) : (
                                                                                                            <img
                                                                                                                src={`${BASE_URL}${iconValue}`}
                                                                                                                width={
                                                                                                                    20
                                                                                                                }
                                                                                                                height={
                                                                                                                    20
                                                                                                                }
                                                                                                                alt="action-icon"
                                                                                                            />
                                                                                                        )
                                                                                                    })()}
                                                                                                </ListItemIcon>

                                                                                                <ListItemText
                                                                                                    primary={voiceiiTags(
                                                                                                        toTitleCase(
                                                                                                            formatDashboardHeaderKey(
                                                                                                                action.value,
                                                                                                            ),
                                                                                                        ),
                                                                                                    )}
                                                                                                />
                                                                                            </MenuItem>
                                                                                        )
                                                                                    },
                                                                                )}

                                                                                {/* Static actions */}
                                                                                {actions.map(
                                                                                    (
                                                                                        action,
                                                                                        index,
                                                                                    ) => {
                                                                                        const isDisabled =
                                                                                            Array.isArray(
                                                                                                row
                                                                                                    ?.values
                                                                                                    ?.action_buttons_visibility,
                                                                                            ) &&
                                                                                            !row.values.action_buttons_visibility.includes(
                                                                                                action?.slug,
                                                                                            )
                                                                                        return (
                                                                                            <MenuItem
                                                                                                className="mobile-action-item"
                                                                                                disabled={
                                                                                                    isDisabled
                                                                                                }
                                                                                                key={
                                                                                                    index
                                                                                                }
                                                                                                onClick={(
                                                                                                    event,
                                                                                                ) => {
                                                                                                    handleMenuClose()
                                                                                                    onActionClick(
                                                                                                        event,
                                                                                                        {
                                                                                                            type: action.type,
                                                                                                            data: row,
                                                                                                        },
                                                                                                    )
                                                                                                }}
                                                                                            >
                                                                                                <ListItemIcon>
                                                                                                    {action.type ===
                                                                                                    "edit" ? (
                                                                                                        <EditIcon
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : action.type ===
                                                                                                      "history" ? (
                                                                                                        <HistoryIcon
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : action.type ===
                                                                                                      "send_instruction" ? (
                                                                                                        <SendIcon
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : action.type ===
                                                                                                      "lock" ? (
                                                                                                        renderLockBtn(
                                                                                                            row,
                                                                                                            iconsColor,
                                                                                                        )
                                                                                                    ) : action.type ===
                                                                                                      "location" ? (
                                                                                                        <NearMeIcon
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : action.type ===
                                                                                                      "end_ride" ? (
                                                                                                        <SettingsIcon
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : action.type ===
                                                                                                      "edit_note" ? (
                                                                                                        <AppRegistrationIcon
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : action.type ==
                                                                                                      "audit_logs" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Audit Logs",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <MenuBookIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "add" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Add",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <AddIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "view" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "View",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <VisibilityOutlinedIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ===
                                                                                                      "more_info" ? (
                                                                                                        <InfoIcon
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : action.type ===
                                                                                                      "mfa_icon" ? (
                                                                                                        <TbAuth2Fa
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : action.type ===
                                                                                                      "lock_user_icon" ? (
                                                                                                        <LockPersonIcon
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : action.type ===
                                                                                                      "update_role_icon" ? (
                                                                                                        <ManageAccountsIcon />
                                                                                                    ) : action.type ===
                                                                                                      "view_file" ? (
                                                                                                        <FaFile
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : (
                                                                                                        <DeleteIcon
                                                                                                            style={{
                                                                                                                color: iconsColor,
                                                                                                            }}
                                                                                                        />
                                                                                                    )}
                                                                                                </ListItemIcon>

                                                                                                <ListItemText
                                                                                                    primary={voiceiiTags(
                                                                                                        toTitleCase(
                                                                                                            action.name,
                                                                                                        ),
                                                                                                    )}
                                                                                                />
                                                                                            </MenuItem>
                                                                                        )
                                                                                    },
                                                                                )}
                                                                            </Menu>
                                                                        </>
                                                                    ) : (
                                                                        /* DESKTOP VIEW */
                                                                        <>
                                                                            <span
                                                                                style={
                                                                                    isAssetDetailScreen
                                                                                        ? {
                                                                                              display:
                                                                                                  "inline-flex",
                                                                                              //   width: `${actionCount * 46}px`,
                                                                                              //   maxWidth: `${actionCount * 46}px`,
                                                                                              paddingLeft:
                                                                                                  "0.3rem",
                                                                                              justifyContent:
                                                                                                  "space-between",
                                                                                          }
                                                                                        : {}
                                                                                }
                                                                            >
                                                                                {row?.values?.action_properties?.map(
                                                                                    (
                                                                                        action,
                                                                                        index,
                                                                                    ) => {
                                                                                        const isHidden =
                                                                                            action?.show !==
                                                                                            true
                                                                                        if (
                                                                                            isHidden
                                                                                        ) {
                                                                                            return null
                                                                                        }

                                                                                        const iconValue =
                                                                                            getActionIconValue(
                                                                                                row.values,
                                                                                                action,
                                                                                            )
                                                                                        const isDisabled =
                                                                                            Array.isArray(
                                                                                                row
                                                                                                    ?.values
                                                                                                    ?.action_buttons_visibility,
                                                                                            ) &&
                                                                                            !row.values.action_buttons_visibility.includes(
                                                                                                action?.slug,
                                                                                            )

                                                                                        const isSvg =
                                                                                            typeof iconValue ===
                                                                                                "string" &&
                                                                                            iconValue
                                                                                                .trim()
                                                                                                .startsWith(
                                                                                                    "<svg",
                                                                                                )

                                                                                        return (
                                                                                            <IconButton
                                                                                                key={
                                                                                                    index
                                                                                                }
                                                                                                disabled={
                                                                                                    isDisabled
                                                                                                }
                                                                                                onClick={(
                                                                                                    event,
                                                                                                ) =>
                                                                                                    onActionClick(
                                                                                                        event,
                                                                                                        {
                                                                                                            type: action.type,
                                                                                                            data: row,
                                                                                                            action_property:
                                                                                                                action,
                                                                                                        },
                                                                                                    )
                                                                                                }
                                                                                                className="dynamic-icon-btn"
                                                                                                variant="secondary"
                                                                                                // size="sm"
                                                                                            >
                                                                                                <Tooltip
                                                                                                    title={voiceiiTags(
                                                                                                        formatDashboardHeaderKey(
                                                                                                            action.value,
                                                                                                        ),
                                                                                                    )}
                                                                                                    placement="top"
                                                                                                    style={{
                                                                                                        color: iconsColor,
                                                                                                    }}
                                                                                                >
                                                                                                    {isSvg ? (
                                                                                                        <span
                                                                                                            className="e-dynamic-action-icon"
                                                                                                            dangerouslySetInnerHTML={{
                                                                                                                __html: applyFillToSvg(
                                                                                                                    iconValue,
                                                                                                                    iconsColor,
                                                                                                                ),
                                                                                                            }}
                                                                                                        />
                                                                                                    ) : (
                                                                                                        <img
                                                                                                            src={`${BASE_URL}${iconValue}`}
                                                                                                            width={
                                                                                                                20
                                                                                                            }
                                                                                                            height={
                                                                                                                20
                                                                                                            }
                                                                                                            alt="action-icon"
                                                                                                        />
                                                                                                    )}
                                                                                                </Tooltip>
                                                                                            </IconButton>
                                                                                        )
                                                                                    },
                                                                                )}
                                                                            </span>

                                                                            {location.pathname ===
                                                                                "/assets-detail" && (
                                                                                <span className="e-table-action-rail-separator"></span>
                                                                            )}

                                                                            {actions.map(
                                                                                (action, index) => {
                                                                                    const isDisabled =
                                                                                        Array.isArray(
                                                                                            row
                                                                                                ?.values
                                                                                                ?.action_buttons_visibility,
                                                                                        ) &&
                                                                                        !row.values.action_buttons_visibility.includes(
                                                                                            action?.slug,
                                                                                        )
                                                                                    if (
                                                                                        action.buttonType ==
                                                                                        "icon"
                                                                                    ) {
                                                                                        if (
                                                                                            location.pathname ===
                                                                                                "/assets-detail" &&
                                                                                            action.type ==
                                                                                                "lock" &&
                                                                                            !row
                                                                                                ?.values
                                                                                                ?.action_properties
                                                                                                ?.length
                                                                                        ) {
                                                                                            {
                                                                                                return (
                                                                                                    <span
                                                                                                        key={
                                                                                                            index
                                                                                                        }
                                                                                                        style={{
                                                                                                            marginLeft:
                                                                                                                "50px",
                                                                                                        }}
                                                                                                    ></span>
                                                                                                )
                                                                                            }
                                                                                        } else {
                                                                                            return (
                                                                                                <IconButton
                                                                                                    key={
                                                                                                        index
                                                                                                    }
                                                                                                    onClick={(
                                                                                                        event,
                                                                                                    ) => {
                                                                                                        if (
                                                                                                            action.type ===
                                                                                                                "lock" &&
                                                                                                            location.pathname ===
                                                                                                                "/assets-detail"
                                                                                                        ) {
                                                                                                            if (
                                                                                                                row
                                                                                                                    ?.values
                                                                                                                    ?.action_properties
                                                                                                                    ?.length >
                                                                                                                0
                                                                                                            ) {
                                                                                                                onActionClick(
                                                                                                                    event,
                                                                                                                    {
                                                                                                                        type: action.type,
                                                                                                                        data: row,
                                                                                                                    },
                                                                                                                )
                                                                                                            }
                                                                                                        } else {
                                                                                                            onActionClick(
                                                                                                                event,
                                                                                                                {
                                                                                                                    type: action.type,
                                                                                                                    data: row,
                                                                                                                },
                                                                                                            )
                                                                                                        }
                                                                                                    }}
                                                                                                    style={{
                                                                                                        marginRight:
                                                                                                            "0px",
                                                                                                        cursor: isPointer(
                                                                                                            action.type,
                                                                                                            row,
                                                                                                        ),
                                                                                                        opacity:
                                                                                                            isDisabled
                                                                                                                ? 0.5
                                                                                                                : 1,
                                                                                                    }}
                                                                                                    variant="secondary"
                                                                                                    size="sm"
                                                                                                    disabled={
                                                                                                        isDisabled
                                                                                                    }
                                                                                                >
                                                                                                    {action.type ==
                                                                                                    "edit" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Edit",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <EditIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "history" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "History",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <HistoryIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "audit_logs" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Audit Logs",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <MenuBookIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "send_instruction" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Send Instruction",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <SendIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "lock" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Lock",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            {renderLockBtn(
                                                                                                                row,
                                                                                                                iconsColor,
                                                                                                            )}
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "location" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Location",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <NearMeIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "end_ride" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "End Ride",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <SettingsIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "edit_note" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Edit Zone",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <AppRegistrationIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "more_info" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "More Info",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <InfoIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "mfa_icon" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "MFA",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <TbAuth2Fa
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                    fontSize:
                                                                                                                        "28px",
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "lock_user_icon" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Lock / Unlock User",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <LockPersonIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                    fontSize:
                                                                                                                        "24px",
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "update_role_icon" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Update Role",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <ManageAccountsIcon />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "view_file" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "View File",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <FaFile
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "add" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Add",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <AddIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : action.type ==
                                                                                                      "view" ? (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "View",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <VisibilityOutlinedIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    ) : (
                                                                                                        <Tooltip
                                                                                                            title={voiceiiTags(
                                                                                                                "Delete",
                                                                                                            )}
                                                                                                            placement="top"
                                                                                                        >
                                                                                                            <DeleteIcon
                                                                                                                style={{
                                                                                                                    color: iconsColor,
                                                                                                                }}
                                                                                                            />
                                                                                                        </Tooltip>
                                                                                                    )}
                                                                                                </IconButton>
                                                                                            )
                                                                                        }
                                                                                    } else
                                                                                        return (
                                                                                            <Button
                                                                                                key={
                                                                                                    index
                                                                                                }
                                                                                                onClick={(
                                                                                                    event,
                                                                                                ) =>
                                                                                                    onActionClick(
                                                                                                        event,
                                                                                                        {
                                                                                                            type: action.type,
                                                                                                            data: row,
                                                                                                        },
                                                                                                    )
                                                                                                }
                                                                                                style={{
                                                                                                    marginRight:
                                                                                                        "10px",
                                                                                                }}
                                                                                                variant="secondary"
                                                                                                size="sm"
                                                                                            >
                                                                                                {
                                                                                                    action.name
                                                                                                }
                                                                                            </Button>
                                                                                        )
                                                                                },
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="pagination-wrapper">
                            <div className="pagination-info">
                                <div>
                                    <span className="me-2">{voiceiiTags("Rows per page")}</span>
                                    <select
                                        className="table-filter-select"
                                        value={pageSize}
                                        onChange={(e) => {
                                            const selectedPageSize = Number(e.target.value)
                                            setPageSize(selectedPageSize)
                                            updateLocalStoragePageSize(
                                                selectedPageSize,
                                                appName,
                                                pathName,
                                            )
                                        }}
                                    >
                                        {[10, 25, 50].map((pageSize) => (
                                            <option key={pageSize} value={pageSize}>
                                                {pageSize}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    {state.pageIndex * pageSize + 1}-
                                    {Math.min((state.pageIndex + 1) * pageSize, rows.length)}{" "}
                                    {voiceiiTags("of")} {rows.length}
                                </div>
                            </div>

                            <div className="pagination-controls">
                                <div
                                    className={`page-link-custom ${!canPreviousPage ? "disabled" : ""}`}
                                    onClick={() => gotoPage(0)}
                                >
                                    {"<<"}
                                </div>
                                <div
                                    className={`page-link-custom ${!canPreviousPage ? "disabled" : ""}`}
                                    onClick={() => previousPage()}
                                >
                                    {"<"}
                                </div>

                                {pageOptions.map((index) => {
                                    if (
                                        index === state.pageIndex ||
                                        index === state.pageIndex - 1 ||
                                        index === state.pageIndex + 1
                                    ) {
                                        return (
                                            <div
                                                key={index}
                                                className={`page-link-custom ${state.pageIndex === index ? "active" : ""}`}
                                                onClick={() => gotoPage(index)}
                                            >
                                                {index + 1}
                                            </div>
                                        )
                                    }
                                    return null
                                })}

                                <div
                                    className={`page-link-custom ${!canNextPage ? "disabled" : ""}`}
                                    onClick={() => nextPage()}
                                >
                                    {">"}
                                </div>
                                <div
                                    className={`page-link-custom ${!canNextPage ? "disabled" : ""}`}
                                    onClick={() => gotoPage(pageCount - 1)}
                                >
                                    {">>"}
                                </div>
                            </div>
                        </div>
                    </div>
                </Box>
                <Modal
                    show={modalStatus}
                    onHide={() => handleModalStatus(false)}
                    size={modalSize}
                    aria-labelledby="react-modal contained-modal-title-vcenter"
                    centered
                >
                    {
                        (datatableTitle = "Device History" ? (
                            ""
                        ) : (
                            <Modal.Header>
                                <Modal.Title id="contained-modal-title-vcenter">
                                    {heading}
                                </Modal.Title>
                                <CloseIcon
                                    className="e-modal-close-icon"
                                    onClick={() => handleModalStatus(false)}
                                />
                            </Modal.Header>
                        ))
                    }

                    <Modal.Body>{modalContent(row, columnMeta)}</Modal.Body>
                    <Modal.Footer>
                        <button
                            className="btn btn-secondary"
                            onClick={() => handleModalStatus(false)}
                        >
                            Close
                        </button>
                    </Modal.Footer>
                </Modal>

                <ModalComponent
                    fullWidth
                    maxWidth="sm"
                    onClose={() => setColumnConfigureModal(false)}
                    modalHeading={"Select and configure columns"}
                    status={columnConfigureModal}
                    disableSave
                    top
                >
                    <div className="w-100">
                        <ColumnToggleSelect
                            allColumns={columns.filter(
                                (c) => !metaDataHiddenColumns.includes(c.id),
                            )}
                            userHiddenColumns={userHiddenColumns}
                            setUserHiddenColumns={setUserHiddenColumns}
                        />
                    </div>
                </ModalComponent>
                <CustomModal
                    status={rangeSliderModal}
                    onHide={() => setRangeSliderModal(false)}
                    maxWidth={"xs"}
                    onClose={() => {
                        setRangeSliderModal(false)
                    }}
                    onSave={() => {
                        setRangeSliderModal(false)
                    }}
                    modalHeading=""
                    aria-labelledby="react-modal contained-modal-title-vcenter"
                    centered
                >
                    <Box>
                        Selected Range is {rangeSliderValue[0]}-{rangeSliderValue[1]}
                    </Box>
                    <Slider
                        value={rangeSliderValue}
                        onChange={handleRangeSliderChange}
                        valueLabelDisplay="auto"
                    />
                    <Box display="flex">
                        <Box fontWeight={"bold"}>Total Assets: &nbsp;&nbsp;&nbsp;</Box>
                        <Box>{rows.length}</Box>
                    </Box>
                    <Box display="flex">
                        <Box fontWeight={"bold"}>Estimated Runout Days: &nbsp;&nbsp;&nbsp;</Box>
                        <Box>
                            {_.sumBy(
                                rows.map((row) => row.values),
                                (object) => object.estimated_runout_days,
                            )}
                        </Box>
                    </Box>
                    <Box display="flex">
                        <Box fontWeight={"bold"}>Amount To Fill Litres:&nbsp;&nbsp;&nbsp; </Box>
                        <Box>
                            {" "}
                            {_.sumBy(
                                rows.map((row) => row.values),
                                (object) => object.amount_to_fill_litres,
                            )}
                        </Box>
                    </Box>
                    <Box display="flex">
                        <Box fontWeight={"bold"}>Remaining Fuel Litres:&nbsp;&nbsp;&nbsp; </Box>
                        <Box>
                            {" "}
                            {_.sumBy(
                                rows.map((row) => row.values),
                                (object) => object.remaining_fuel_litres,
                            )}
                        </Box>
                    </Box>
                </CustomModal>
            </>
        )
    },
)

export default DataTable

DataTable.defaultProps = {
    memonizedFilters: [],
    buttons: [],
    modalStatus: false,
    datatableTitle: "",
    hasFilterAffectsMap: true,
    modalHeading: "",
    isMultiSelect: false,
    onSelectedRowsChange: null,
    actions: [],
    modalSize: "lg",
    columns: [],
    data: [],
    modalContent() {},
    refreshAction() {},
    filteredResult() {},
}
